import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FormInput } from "../components/inputs/form-input";
import { useEffect } from "react";
import { BeatLoader, ClipLoader } from "react-spinners";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  getLoggedInUser,
  updateUser,
  updateUserPassword,
} from "../services/userService";
import { IUser } from "../types";
import { z } from "zod";
import { useAppDispatch } from "../data/store/hooks";
import { loadUser } from "../data/store/reducers/userSlice";
import UploadImage from "../components/inputs/image-uploads";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required!"),
  lastName: z.string().min(1, "Last name is required!"),
  email: z.string().email("Invalid email address!"),
  phone: z.string().optional(),
  gender: z.string().optional(),
  role: z.string().optional(),
  userStatus: z.string().optional(),
  emailVerified: z.string().optional(),
  about: z.string().optional(),
});

export type ProfileFormData = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  oldPassword: z.string().min(1, "Current password is required!"),
  newPassword: z.string().min(6, "Password must be at least 6 characters!"),
});

type PasswordFormData = z.infer<typeof passwordSchema>;

const Settings = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery<IUser, Error>({
    queryKey: ["loggedInUser"],
    queryFn: getLoggedInUser,
  });

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    if (user) {
      const profileData: ProfileFormData = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        gender: user.gender,
        role: user.role,
        userStatus: user.userStatus,
        emailVerified: user.emailVerified ?? undefined,
        about: user.about,
      };
      resetProfile(profileData);
    }
  }, [user, resetProfile]);

  const { mutate: updateProfile, isPending: isProfilePending } = useMutation<
    { success: string },
    { response: { data: { error: string } } },
    ProfileFormData
  >({
    mutationFn: updateUser,
    onSuccess: (res) => {
      toast.success(res.success);
      resetProfile();
      queryClient.invalidateQueries({ queryKey: ["loggedInUser"] });
      dispatch(loadUser());
    },
    onError: (error) => {
      toast.error(error.response.data.error);
    },
  });

  const { mutate: updatePassword, isPending: isPasswordPending } = useMutation<
    { success: string },
    { response: { data: { error: string } } },
    PasswordFormData
  >({
    mutationFn: updateUserPassword,
    onSuccess: (res) => {
      toast.success(res.success);
      resetPassword();
    },
    onError: (error) => {
      toast.error(error.response.data.error);
    },
  });

  const onProfileSubmit: SubmitHandler<ProfileFormData> = async (data) => {
    updateProfile(data);
  };

  const onPasswordSubmit: SubmitHandler<PasswordFormData> = (data) => {
    updatePassword(data);
  };

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="mt-4 px-2 md:px-4 max-w-3xl mx-auto flex flex-col space-y-4">
      <div>
        <h2 className="text-xl mb-4">Profile</h2>
        <div className="flex flex-col lg:flex-row lg:space-x-4">
          <UploadImage userImage={user?.image} userData={user} />
          <div className="flex-1 mt-4">
            <form
              className="flex flex-col space-y-2"
              onSubmit={handleProfileSubmit(onProfileSubmit)}
            >
              <div className="flex flex-col space-y-1 md:flex-row md:space-x-3 md:space-y-0">
                <FormInput
                  name="firstName"
                  label="First Name"
                  register={registerProfile}
                  error={profileErrors.firstName?.message}
                />
                <FormInput
                  name="lastName"
                  label="Last Name"
                  register={registerProfile}
                  error={profileErrors.lastName?.message}
                />
              </div>
              <div className="flex flex-col space-y-1 md:flex-row md:space-x-3 md:space-y-0">
                <FormInput
                  name="email"
                  label="Admin Email"
                  register={registerProfile}
                  error={profileErrors.email?.message}
                />
                <FormInput
                  name="phone"
                  label="Phone Number"
                  register={registerProfile}
                  error={profileErrors.phone?.message}
                />
              </div>
              <div className="flex flex-col space-y-1 md:flex-row md:space-x-3 md:space-y-0">
                <FormInput
                  name="gender"
                  label="Gender"
                  register={registerProfile}
                  error={profileErrors.gender?.message}
                />
                <FormInput
                  name="role"
                  label="Role"
                  disabled
                  register={registerProfile}
                  error={profileErrors.role?.message}
                />
              </div>
              <div className="flex flex-col space-y-1 md:flex-row md:space-x-3 md:space-y-0">
                <FormInput
                  name="userStatus"
                  label="User status"
                  disabled
                  register={registerProfile}
                  error={profileErrors.userStatus?.message}
                />
                <FormInput
                  name="emailVerified"
                  label="Email verified"
                  disabled
                  register={registerProfile}
                  error={profileErrors.emailVerified?.message}
                />
              </div>
              <FormInput
                name="about"
                label="About admin"
                register={registerProfile}
                error={profileErrors.about?.message}
              />

              <button
                type="submit"
                className="border px-2 py-3 rounded-lg text-sm flex items-center justify-center"
                disabled={isProfilePending}
              >
                {isProfilePending ? <ClipLoader size={20} /> : "Update"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl mb-4">Change Password</h2>
        <form
          className="flex flex-col space-y-2"
          onSubmit={handlePasswordSubmit(onPasswordSubmit)}
        >
          <FormInput
            name="oldPassword"
            label="Current password"
            placeholder="******"
            type="password"
            register={registerPassword}
            error={passwordErrors.oldPassword?.message}
          />
          <FormInput
            name="newPassword"
            label="New password"
            placeholder="******"
            type="password"
            register={registerPassword}
            error={passwordErrors.newPassword?.message}
          />

          <button
            type="submit"
            className="border px-6 py-3 rounded-lg w-max text-sm flex items-center justify-center"
            disabled={isPasswordPending}
          >
            {isPasswordPending ? <ClipLoader size={20} /> : "Update"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
